import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { ChartLineUp, Calendar, UsersFour, CheckCircle, Warning, Clock, CurrencyInr, FileText, PaperPlaneTilt, FilePdf } from '@phosphor-icons/react';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { logError, logWarn } from '@/lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtInr = (n) => `₹${(Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const todayMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const Reports = () => {
  const [month, setMonth] = useState(todayMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreements, setAgreements] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [reportRes, agreementsRes] = await Promise.all([
        axios.get(`${API}/reports/monthly`, { params: { month } }),
        axios.get(`${API}/agreements/monthly`, { params: { month } }),
      ]);
      setData(reportRes.data);
      setAgreements(agreementsRes.data.candidates || []);
    } catch (e) {
      toast.error('Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const monthLabel = useMemo(() => {
    try { return format(parseISO(`${month}-01`), 'MMMM yyyy'); } catch { return month; }
  }, [month]);

  const handleSendAgreement = async (a) => {
    if (!a.whatsapp_url) {
      toast.error(`No phone number for ${a.candidate_name}`);
      return;
    }
    window.open(a.whatsapp_url, '_blank', 'noopener,noreferrer');
    try {
      await axios.post(`${API}/candidates/${a.candidate_id}/agreement/sent`);
      fetchData();
    } catch (markErr) {
      logWarn('Failed to mark agreement as sent:', markErr?.response?.data?.detail || markErr.message);
    }
  };

  const handleDownloadPdf = async (a) => {
    try {
      const res = await axios.get(`${API}/candidates/${a.candidate_id}/agreement.pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `TulsiJobs_Agreement_${a.candidate_name.replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      try {
        await axios.post(`${API}/candidates/${a.candidate_id}/agreement/sent`);
        fetchData();
      } catch (markErr) {
        logWarn('Failed to mark agreement as sent:', markErr?.response?.data?.detail || markErr.message);
      }
      toast.success('Agreement PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const handleSendAll = () => {
    const eligible = agreements.filter(a => a.whatsapp_url);
    if (eligible.length === 0) {
      toast.error('No candidates with phone numbers to send to');
      return;
    }
    if (!window.confirm(`This will open ${eligible.length} WhatsApp tabs (one per candidate). Continue?`)) return;
    eligible.forEach((a, i) => {
      setTimeout(async () => {
        window.open(a.whatsapp_url, '_blank', 'noopener,noreferrer');
        try { await axios.post(`${API}/candidates/${a.candidate_id}/agreement/sent`); } catch (e) { logWarn('agreement-stamp', `Failed to stamp ${a.candidate_name}: ${e?.message || e}`); }
      }, i * 400);
    });
    setTimeout(fetchData, eligible.length * 400 + 600);
    toast.success(`Opening ${eligible.length} WhatsApp chats…`);
  };

  return (
    <div data-testid="reports-page">
      <div className="mb-8 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-4xl sm:text-5xl font-black tracking-tighter text-white mb-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
            Reports
          </h1>
          <p className="text-base font-medium text-white">Month-wise recruiter activity & candidate breakdown</p>
        </div>
        <div className="flex items-center gap-3">
          <Calendar size={24} weight="duotone" className="text-[#A3E635]" />
          <Input
            data-testid="reports-month-input"
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="bg-white border-2 border-[#A3E635] rounded-md px-3 py-2 text-black font-bold"
          />
        </div>
      </div>

      {loading || !data ? (
        <div className="text-white font-bold">Loading…</div>
      ) : (
        <>
          {/* Totals */}
          <h2 className="text-xl font-bold text-white mb-3" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>{monthLabel} Totals</h2>
          <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 mb-8">
            <TotalCard testid="reports-total-added" bg="#93C5FD" icon={UsersFour} label="Added" value={data.totals.candidates_added} />
            <TotalCard testid="reports-total-joined" bg="#A3E635" icon={CheckCircle} label="Joined" value={data.totals.joined} />
            <TotalCard testid="reports-total-pending" bg="#FDE047" icon={Clock} label="Pending" value={data.totals.pending} />
            <TotalCard testid="reports-total-fired-left" bg="#F87171" icon={Warning} label="Fired / Left" value={data.totals.fired_or_left} />
            <TotalCard testid="reports-total-billed" bg="#C4B5FD" icon={CurrencyInr} label="Total Billed" value={fmtInr(data.totals.total_billed)} />
            <TotalCard testid="reports-total-collected" bg="#F9A8D4" icon={ChartLineUp} label="Collected" value={fmtInr(data.totals.collected_this_month)} />
          </div>

          {/* Per-recruiter activity */}
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
            <UsersFour size={24} weight="duotone" className="text-[#A3E635]" />
            Recruiter Activity
          </h2>
          <div className="bg-[#0F3622] border-2 border-[#A3E635] rounded-md shadow-[4px_4px_0px_0px_rgba(163,230,53,0.3)] overflow-x-auto mb-8">
            <table className="w-full tracker-table" data-testid="recruiter-activity-table">
              <thead>
                <tr>
                  <th>Recruiter</th>
                  <th>Added</th>
                  <th>Joined</th>
                  <th>Pending</th>
                  <th>Fired/Left</th>
                  <th>Billed</th>
                  <th>Collected</th>
                </tr>
              </thead>
              <tbody>
                {data.recruiter_activity.length === 0 ? (
                  <tr><td colSpan="7" className="text-center py-8 text-white">No recruiter activity in {monthLabel}.</td></tr>
                ) : (
                  data.recruiter_activity.map((r) => (
                    <tr key={r.recruiter} data-testid={`recruiter-row-${r.recruiter}`}>
                      <td className="font-bold">{r.recruiter}</td>
                      <td>{r.candidates_added}</td>
                      <td className={r.joined > 0 ? 'font-bold text-[#A3E635]' : ''}>{r.joined}</td>
                      <td>{r.pending}</td>
                      <td className={r.fired_or_left > 0 ? 'font-bold text-[#F87171]' : ''}>{r.fired_or_left}</td>
                      <td className="font-bold">{fmtInr(r.total_billed)}</td>
                      <td className={r.collected_this_month > 0 ? 'font-bold text-[#A3E635]' : ''}>{fmtInr(r.collected_this_month)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Per-candidate breakdown */}
          <h2 className="text-xl font-bold text-white mb-3 flex items-center gap-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
            <Calendar size={24} weight="duotone" className="text-[#FDE047]" />
            Candidates this Month
          </h2>
          <div className="bg-[#0F3622] border-2 border-[#FDE047] rounded-md shadow-[4px_4px_0px_0px_rgba(253,224,71,0.3)] overflow-x-auto">
            <table className="w-full tracker-table" data-testid="reports-candidates-table">
              <thead>
                <tr>
                  <th>Candidate</th>
                  <th>Company</th>
                  <th>Recruiter</th>
                  <th>Interview</th>
                  <th>Joining</th>
                  <th>Status</th>
                  <th>Billed</th>
                  <th>Collected</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {data.candidates.length === 0 ? (
                  <tr><td colSpan="9" className="text-center py-8 text-white">No candidates active in {monthLabel}.</td></tr>
                ) : (
                  data.candidates.map((c) => (
                    <tr key={c.id} data-testid={`reports-candidate-row-${c.id}`}>
                      <td className="font-bold">{c.candidate_name}</td>
                      <td>{c.company_name}</td>
                      <td>{c.recruiter_name}</td>
                      <td>{c.interview_date ? format(parseISO(c.interview_date), 'dd MMM') : '—'}</td>
                      <td>{c.date_of_joining ? format(parseISO(c.date_of_joining), 'dd MMM') : '—'}</td>
                      <td>
                        <span className="px-2 py-1 bg-[#C4B5FD] text-black rounded text-xs font-bold border-2 border-black uppercase">
                          {c.joining_status}
                        </span>
                      </td>
                      <td className="font-bold">
                        {c.joining_in_month ? fmtInr(c.effective_charge) : '—'}
                        {c.has_override && c.joining_in_month && (
                          <span className="ml-1 px-1.5 py-0.5 bg-[#F59E0B] text-black rounded text-[10px] font-bold align-middle">ADJ</span>
                        )}
                      </td>
                      <td className={c.paid_this_month > 0 ? 'font-bold text-[#A3E635]' : ''}>{fmtInr(c.paid_this_month)}</td>
                      <td className={c.balance > 0.01 ? 'font-bold text-[#FDE047]' : 'font-bold text-[#A3E635]'}>{fmtInr(c.balance)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Monthly Agreements */}
          <div className="mt-8" data-testid="monthly-agreements-section">
            <div className="flex items-end justify-between mb-3">
              <div>
                <h2 className="text-xl font-bold text-white mb-1 flex items-center gap-2" style={{fontFamily: 'Cabinet Grotesk, sans-serif'}}>
                  <FileText size={24} weight="duotone" className="text-[#A3E635]" />
                  Monthly Agreements
                </h2>
                <p className="text-sm text-white">Pre-filled employment agreements ready to send on WhatsApp · {agreements.length} candidate{agreements.length !== 1 ? 's' : ''} joining in {monthLabel}</p>
              </div>
              {agreements.length > 0 && (
                <button
                  data-testid="send-all-agreements"
                  onClick={handleSendAll}
                  className="bg-[#A3E635] text-black border-2 border-black rounded-md font-bold px-4 py-2 shadow-[3px_3px_0px_0px_rgba(10,10,10,1)] hover:-translate-y-0.5 hover:shadow-[4px_4px_0px_0px_rgba(10,10,10,1)] transition-all flex items-center gap-2"
                >
                  <PaperPlaneTilt size={18} weight="fill" />
                  Send All ({agreements.filter(a => a.whatsapp_url).length})
                </button>
              )}
            </div>

            <div className="bg-[#0F3622] border-2 border-[#A3E635] rounded-md shadow-[4px_4px_0px_0px_rgba(163,230,53,0.3)] overflow-x-auto">
              <table className="w-full tracker-table" data-testid="monthly-agreements-table">
                <thead>
                  <tr>
                    <th>Candidate</th>
                    <th>Company</th>
                    <th>Position</th>
                    <th>Salary</th>
                    <th>Joining</th>
                    <th>Phone</th>
                    <th>Agreement</th>
                  </tr>
                </thead>
                <tbody>
                  {agreements.length === 0 ? (
                    <tr><td colSpan="7" className="text-center py-6 text-white">No candidates joining in {monthLabel}.</td></tr>
                  ) : (
                    agreements.map((a) => (
                      <tr key={a.candidate_id} data-testid={`agreement-row-${a.candidate_id}`}>
                        <td className="font-bold">{a.candidate_name}</td>
                        <td>{a.company_name}</td>
                        <td>{a.job_category || <span className="text-[#F87171] text-xs">missing</span>}</td>
                        <td className="font-bold">{a.salary > 0 ? fmtInr(a.salary) : <span className="text-[#F87171] text-xs">missing</span>}</td>
                        <td>{a.date_of_joining ? format(parseISO(a.date_of_joining), 'dd MMM') : '—'}</td>
                        <td className="text-xs">{a.phone || <span className="text-[#F87171]">missing</span>}</td>
                        <td>
                          <div className="flex flex-wrap gap-1.5 items-center">
                            <button
                              data-testid={`send-agreement-${a.candidate_id}`}
                              onClick={() => handleSendAgreement(a)}
                              disabled={!a.whatsapp_url}
                              className={`inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-bold border-2 transition-all ${
                                a.whatsapp_url
                                  ? 'bg-[#25D366] text-black border-black hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] cursor-pointer'
                                  : 'bg-[#404040] text-white border-[#404040] cursor-not-allowed'
                              }`}
                            >
                              <PaperPlaneTilt size={14} weight="fill" />
                              Send
                            </button>
                            <button
                              data-testid={`download-pdf-${a.candidate_id}`}
                              onClick={() => handleDownloadPdf(a)}
                              title="Download PDF (attach manually in WhatsApp)"
                              className="inline-flex items-center gap-1 px-3 py-1 rounded-md text-xs font-bold border-2 border-black bg-[#F87171] text-white hover:-translate-y-0.5 hover:shadow-[2px_2px_0px_0px_rgba(10,10,10,1)] cursor-pointer transition-all"
                            >
                              <FilePdf size={14} weight="fill" />
                              PDF
                            </button>
                            {a.agreement_sent_at && (
                              <span
                                data-testid={`sent-badge-${a.candidate_id}`}
                                title={`Sent on ${format(parseISO(a.agreement_sent_at), 'dd MMM yyyy, HH:mm')}`}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold border-2 border-[#A3E635] bg-[#0B2818] text-[#A3E635]"
                              >
                                <CheckCircle size={12} weight="fill" />
                                SENT {format(parseISO(a.agreement_sent_at), 'dd MMM')}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const TotalCard = ({ testid, bg, icon: Icon, label, value }) => (
  <div
    data-testid={testid}
    className="border-2 border-black rounded-md p-3 shadow-[3px_3px_0px_0px_rgba(163,230,53,0.4)]"
    style={{ backgroundColor: bg }}
  >
    <div className="flex items-start justify-between mb-2">
      <Icon size={20} weight="duotone" />
    </div>
    <div className="text-xl font-black text-black">{value}</div>
    <div className="text-[10px] font-bold uppercase tracking-wider text-black">{label}</div>
  </div>
);

export default Reports;
